import React from "react";
import { ShieldCheck, Award, Landmark, Lock, Database, Eye, ScrollText, FlaskConical } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/ui-bits";
import { PageHero, Section } from "@/components/PageShell";

const APPROVALS = [
  [ShieldCheck, "SEZA Board Approval", "Approval to operate internationally traded diamond services at Diamond Park."],
  [Award, "BITC & the Botswana Pride Mark", "Authorization and support from BITC concerning the Botswana Pride mark."],
  [Landmark, "Office of the President", "Endorsement permitting the optional engraving of a verified, invisible Botswana Pride mark into eligible Botswana diamonds."],
];

const PRINCIPLES = [
  [Lock, "Privacy & security", "Encrypted documents and records, multi-factor authentication, role-based access and controlled ownership changes."],
  [Database, "Data-provider transparency", "Each certificate clearly shows which services and records apply to that particular diamond."],
  [Eye, "Public vs confidential", "A strict separation between authorised public information and confidential participant data."],
  [ScrollText, "Compliance principles", "Time-stamped audit history and administrator approval for material updates. No certificate is issued automatically."],
];

export default function TrustGovernance() {
  return (
    <div>
      <PageHero
        eyebrow="Trust & Governance"
        title="Credible enough for institutions. Clear about what we verify."
        subtitle="LRG Botswana Brilliance is a Botswana-owned, SEZA-approved diamond-services company operating independently in collaboration with licensed industry participants and approved technology providers."
      />

      <Section>
        <Reveal><Eyebrow>Institutional foundation</Eyebrow></Reveal>
        <div className="mt-8 grid md:grid-cols-3 gap-5">
          {APPROVALS.map(([Icon, t, d], i) => (
            <Reveal key={t} delay={i * 0.1}>
              <div className="glass rounded-2xl p-8 h-full">
                <Icon className="h-8 w-8 text-bb-gold" strokeWidth={0.9} />
                <h3 className="mt-6 font-serif text-2xl leading-tight">{t}</h3>
                <p className="mt-3 text-sm text-bb-silver/65 leading-relaxed">{d}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <p className="mt-8 text-sm text-bb-silver/50 max-w-3xl leading-relaxed">
            Botswana Brilliance is <span className="text-bb-silver">not</span> a government department, government-owned company or
            official national authority. It operates independently and works with multiple manufacturers, dealers, brokers,
            laboratories, technology providers and international market partners.
          </p>
        </Reveal>
      </Section>

      <Section className="pt-0">
        <Reveal><Eyebrow>Compliance & security</Eyebrow></Reveal>
        <div className="mt-8 grid sm:grid-cols-2 gap-5">
          {PRINCIPLES.map(([Icon, t, d], i) => (
            <Reveal key={t} delay={i * 0.08}>
              <div className="glass rounded-2xl p-7 flex gap-5 h-full">
                <Icon className="h-6 w-6 text-bb-gold shrink-0" strokeWidth={1} />
                <div>
                  <h3 className="font-serif text-2xl">{t}</h3>
                  <p className="mt-2 text-sm text-bb-silver/65 leading-relaxed">{d}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section className="pt-0">
        <Reveal>
          <div className="glass rounded-2xl p-8 md:p-12 flex flex-col md:flex-row gap-8">
            <FlaskConical className="h-12 w-12 text-bb-gold shrink-0" strokeWidth={0.9} />
            <div>
              <h2 className="font-serif text-3xl md:text-4xl">What we verify, and what we do not.</h2>
              <p className="mt-4 text-sm text-bb-silver/70 leading-relaxed max-w-3xl">
                Botswana Brilliance reviews and connects the supporting records associated with a diamond's documented
                Botswana-origin, physical identity, chain of custody and journey. Each certificate identifies the source of the
                information, the evidence supplied and the independent service provider responsible for any laboratory, identity
                or technology record. Independent grading remains the responsibility of the issuing laboratory.
              </p>
              <p className="mt-4 text-sm text-bb-silver/50 leading-relaxed max-w-3xl">
                Botswana Brilliance does not own every listed diamond and does not replace independent laboratories.
              </p>
            </div>
          </div>
        </Reveal>
      </Section>
    </div>
  );
}
